import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Shield,
  Users,
  Building2,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Scale,
  Briefcase,
  HeartHandshake,
  BarChart3,
  Globe,
  Layers,
  Lock,
  Bot,
  UserCheck,
  Hospital,
  Wallet,
  ChevronRight,
  Info,
} from "lucide-react";
import { MarketingLayout } from "@/components/marketing-layout";


const leadershipRoles = [
  {
    icon: Briefcase,
    title: "Chief Executive",
    owns: "Strategic direction, capital allocation, and overall regulatory posture of the platform.",
  },
  {
    icon: Scale,
    title: "Chief Compliance Officer",
    owns: "All compliance flags, regulatory filings, and enforcement of platform scope boundaries.",
  },
  {
    icon: Shield,
    title: "General Counsel",
    owns: "Hospital contracts, employer agreements, and all regulatory language across the platform.",
  },
  {
    icon: HeartHandshake,
    title: "Partner Executives",
    owns: "Hospital C-suite relationships and enterprise employer deals — relationships that require human trust.",
  },
];

const capabilities = [
  {
    icon: Wallet,
    title: "Member & Community Operations",
    description: "Wallet management, contribution routing, community fund oversight, and referral programs for individual members.",
  },
  {
    icon: Building2,
    title: "Employer & Hospital Operations",
    description: "Small business onboarding, pre-funded wallet creation, and payment arrangement management with hospital partners.",
  },
  {
    icon: BarChart3,
    title: "Financial Integrity",
    description: "Transaction reconciliation, fraud detection, and platform fee monitoring across all payment flows.",
  },
  {
    icon: Globe,
    title: "Growth & Content",
    description: "Marketing content, investor pipeline tracking, and multi-language quality assurance across EN/ES/SO/HMN.",
  },
  {
    icon: Lock,
    title: "Platform Integrity",
    description: "Feature flag governance, audit log review, and compliance monitoring to keep platform scope clean.",
  },
];

const populations = [
  {
    icon: Users,
    title: "Individuals",
    subtitle: "The uninsured and underinsured workforce",
    description: "30 million+ workers without employer health coverage. HealthWallet creates community funding mechanisms and payment infrastructure to help them fund and pay for care costs.",
    stat: "30M+ workers",
    testId: "card-serve-individuals",
  },
  {
    icon: Briefcase,
    title: "Employers",
    subtitle: "Small businesses with 10–200 employees",
    description: "Companies that want to support their workers' health costs but can't navigate traditional benefits infrastructure. HealthWallet gives them a direct contribution channel.",
    stat: "No benefits plan required",
    testId: "card-serve-employers",
  },
  {
    icon: Hospital,
    title: "Hospital Partners",
    subtitle: "Healthcare facilities in the network",
    description: "Hospitals that use HealthWallet to manage pre-funded patient wallets, emergency funding requests, and post-care payment arrangements with their patients.",
    stat: "Twin Cities metro, expanding",
    testId: "card-serve-hospitals",
  },
];

const weAre = [
  "Payment infrastructure",
  "Funding facilitation platform",
  "Technology provider",
  "Community contribution network",
];

const weAreNot = [
  "Insurance company",
  "Benefits plan administrator",
  "Clinical service",
  "Medical necessity assessor",
];

export default function AboutPage() {
  usePageTitle("About HealthWallet", "Learn how HealthWallet is building health infrastructure for the uninsured workforce in Minnesota.");

  return (
    <div className="min-h-screen bg-background">
      <MarketingLayout>
      <section
        className="relative min-h-[520px] flex items-center justify-center overflow-hidden"
        data-testid="section-about-hero"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-primary/30" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/20 via-transparent to-transparent" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center">
          <Badge className="mb-6 bg-primary/20 text-primary-foreground border-primary/30" data-testid="badge-about">
            <Info className="w-3 h-3 mr-1.5" />
            About HealthWallet
          </Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" data-testid="heading-about-hero">
            Payment and funding infrastructure<br className="hidden sm:block" /> for the uninsured workforce
          </h1>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed" data-testid="text-about-subheadline">
            HealthWallet moves money, facilitates contributions, and manages financial arrangements between members, employers, hospitals, and communities. We are not a healthcare provider. We are the funding layer underneath one.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link href="/pitch">
              <Button size="lg" data-testid="button-hero-pitch">
                Investor Pitch
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link href="/pilot-apply">
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white" data-testid="button-hero-pilot">
                Apply for Pilot
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-muted/30" data-testid="section-mission">
        <div className="max-w-3xl mx-auto text-center">
          <Badge variant="outline" className="mb-4">Our Mission</Badge>
          <h2 className="text-2xl sm:text-3xl font-bold mb-6" data-testid="heading-mission">What We Do</h2>
          <p className="text-muted-foreground text-lg leading-relaxed mb-6">
            HealthWallet exists to solve one problem: 30 million workers in the United States have no pathway to fund their healthcare costs. They are not inside the insurance system. They pay cash, delay care, and negotiate hospital bills alone.
          </p>
          <p className="text-muted-foreground text-lg leading-relaxed">
            We build the infrastructure that creates that pathway — community wallets, employer contributions, hospital payment arrangements, and emergency funding — all without touching a single clinical decision. That belongs to providers and patients. We handle the money.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 sm:px-6" data-testid="section-leadership">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Human Governance</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" data-testid="heading-leadership">The Team That Sets the Rules</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              A small senior team owns all judgment calls, regulatory accountability, and partner relationships. AI handles execution. Humans handle everything that matters most.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
            {leadershipRoles.map((role) => (
              <Card key={role.title} data-testid={`card-leadership-${role.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <CardContent className="p-6 flex gap-4">
                  <div className="p-2.5 rounded-md bg-primary/10 h-fit shrink-0">
                    <role.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{role.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{role.owns}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground" data-testid="text-leadership-note">
            Leadership profiles are available upon request for investors and prospective partners.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-muted/30" data-testid="section-how-we-work">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Operating Model</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" data-testid="heading-how-we-work">AI Operates. Humans Decide.</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              HealthWallet is designed to be run by AI agents with human supervision — giving a small team the operational leverage to serve a massive underserved population.
            </p>
          </div>

          <Card className="overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
              <CardContent className="p-8" data-testid="panel-human-team">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-md bg-primary/10">
                    <UserCheck className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">Human Team</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    "Set all rules and regulatory guardrails",
                    "Own every judgment call",
                    "Approve irreversible actions before they execute",
                    "Hold all partner and investor relationships",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <ChevronRight className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span className="text-sm text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardContent className="p-8" data-testid="panel-ai-operations">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-md bg-primary/10">
                    <Bot className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">AI Operations</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    "Execute every routine operation within defined rules",
                    "Handle the volume and speed a human team cannot",
                    "Surface data and anomalies for human decisions",
                    "Log every action for complete audit traceability",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <ChevronRight className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span className="text-sm text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </div>
          </Card>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: "Human roles", value: "4", sub: "Own all judgment" },
              { label: "Operational tiers", value: "5", sub: "AI-managed" },
              { label: "Escalation levels", value: "4", sub: "Autonomous → Escalate" },
            ].map((stat) => (
              <Card key={stat.label}>
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-primary mb-1" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}>{stat.value}</div>
                  <div className="font-medium text-sm">{stat.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{stat.sub}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 sm:px-6" data-testid="section-capabilities">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Operational Capabilities</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" data-testid="heading-capabilities">Five Areas. One Platform.</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              AI agents handle execution in each area under continuous human oversight. No clinical judgment. No insurance function. Pure funding infrastructure.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {capabilities.map((cap, i) => (
              <Card key={cap.title} data-testid={`card-capability-${i + 1}`}>
                <CardContent className="p-6">
                  <div className="p-2.5 rounded-md bg-primary/10 w-fit mb-4">
                    <cap.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{cap.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{cap.description}</p>
                </CardContent>
              </Card>
            ))}

            <Card className="sm:col-span-2 lg:col-span-1 border-primary/20 bg-primary/5" data-testid="card-capability-escalation">
              <CardContent className="p-6">
                <div className="p-2.5 rounded-md bg-primary/10 w-fit mb-4">
                  <Layers className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold mb-3">Escalation Framework</h3>
                <div className="space-y-2">
                  {[
                    { level: "Autonomous", desc: "Routine operations" },
                    { level: "Notify", desc: "Unusual patterns" },
                    { level: "Approve", desc: "Irreversible actions" },
                    { level: "Escalate", desc: "Regulatory risk" },
                  ].map((e) => (
                    <div key={e.level} className="flex items-center justify-between text-sm">
                      <span className="font-medium">{e.level}</span>
                      <span className="text-muted-foreground text-xs">{e.desc}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-muted/30" data-testid="section-who-we-serve">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Who We Serve</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" data-testid="heading-who-we-serve">Three Populations. One Problem.</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              The funding gap in healthcare costs affects individuals, the small businesses they work for, and the hospitals that serve them. HealthWallet sits at the intersection of all three.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {populations.map((pop) => (
              <Card key={pop.title} data-testid={pop.testId}>
                <CardContent className="p-6">
                  <div className="p-2.5 rounded-md bg-primary/10 w-fit mb-4">
                    <pop.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-1">{pop.title}</h3>
                  <p className="text-xs text-muted-foreground font-medium mb-3">{pop.subtitle}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{pop.description}</p>
                  <div className="pt-3 border-t border-border">
                    <span className="text-xs font-semibold text-foreground">{pop.stat}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 sm:px-6" data-testid="section-regulatory">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Regulatory Commitment</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" data-testid="heading-regulatory">What We Are. What We're Not.</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Our regulatory positioning is not a disclaimer — it is a design decision embedded in every feature, every agent behavior, and every line of code.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
            <Card data-testid="card-we-are">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  What we are
                </h3>
                <ul className="space-y-3">
                  {weAre.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card data-testid="card-we-are-not">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-muted-foreground" />
                  What we are not
                </h3>
                <ul className="space-y-3">
                  {weAreNot.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-sm text-muted-foreground">
                      <XCircle className="w-4 h-4 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <p className="text-xs text-muted-foreground text-center leading-relaxed max-w-3xl mx-auto" data-testid="text-legal-disclaimer">
            HealthWallet is a healthcare funding technology platform. HealthWallet is not a bank, insurance company, or healthcare provider. All funds are processed and held by Stripe, Inc. Payment schedules are facilitated by participating healthcare providers. Pre-care subscriptions are voluntary savings mechanisms, not insurance products.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-slate-900" data-testid="section-about-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to learn more?
          </h2>
          <p className="text-slate-400 mb-8 text-lg">
            See the full investor case for HealthWallet or apply to join our Minnesota pilot program.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link href="/pitch">
              <Button size="lg" data-testid="button-cta-pitch">
                Investor Pitch Deck
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link href="/pilot-apply">
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white" data-testid="button-cta-pilot">
                Apply for Pilot
              </Button>
            </Link>
          </div>
        </div>
      </section>

      </MarketingLayout>
    </div>
  );
}
